import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import {
  LayoutDashboard,
  Grid3X3,
  Layers,
  Scissors,
  Shirt,
  Tag,
  Ruler,
  Palette,
  Users,
  History,
  LogOut,
  Menu,
  X,
  Home,
  GitBranch
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Contexto para permisos de Muestras
const MuestrasPermissionContext = createContext(null);

export const useMuestrasPermissions = () => {
  const context = useContext(MuestrasPermissionContext);
  if (!context) {
    return { permissions: {}, isAdmin: false, loading: true };
  }
  return context;
};

// Permisos por defecto (todo false)
const defaultPermissions = {
  marcas_ver: false, marcas_crear: false, marcas_editar: false, marcas_eliminar: false,
  tipos_ver: false, tipos_crear: false, tipos_editar: false, tipos_eliminar: false,
  entalles_ver: false, entalles_crear: false, entalles_editar: false, entalles_eliminar: false,
  telas_ver: false, telas_crear: false, telas_editar: false, telas_eliminar: false,
  hilos_ver: false, hilos_crear: false, hilos_editar: false, hilos_eliminar: false,
  tizados_ver: false, tizados_crear: false, tizados_editar: false, tizados_eliminar: false,
  muestras_ver: false, muestras_crear: false, muestras_editar: false, muestras_eliminar: false,
  bases_ver: false, bases_crear: false, bases_editar: false, bases_eliminar: false,
  descargar_archivos: false, subir_archivos: false
};

const MuestrasLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  
  // Usar autenticación global
  const { user, logout } = useApp();

  // Verificar si es admin del sistema
  const isSystemAdmin = user?.rol === 'ADMIN' || user?.rol === 'admin' || user?.rol === 'super_admin' || user?.rol === 'SUPER_ADMIN';

  // Cargar permisos del usuario para Muestras
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id) {
        setPermissionsLoading(false);
        return;
      }

      // Si es admin del sistema, tiene todos los permisos
      if (isSystemAdmin) {
        const allTrue = Object.keys(defaultPermissions).reduce((acc, key) => ({ ...acc, [key]: true }), {});
        setPermissions(allTrue);
        setPermissionsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/api/muestras/usuarios/${user.id}/permisos`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Combinar con defaults para asegurar todas las keys existen
          setPermissions({ ...defaultPermissions, ...data });
        }
      } catch (error) {
        console.error('Error fetching muestras permissions:', error);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissions();
  }, [user?.id, isSystemAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  
  // isAdmin para el módulo Muestras (admin del sistema)
  const isAdmin = isSystemAdmin;

  const menuItems = [
    { path: '/muestras/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/muestras/marcas', icon: Palette, label: 'Marcas' },
    { path: '/muestras/tipos', icon: Tag, label: 'Tipos Producto' },
    { path: '/muestras/entalles', icon: Ruler, label: 'Entalles' },
    { path: '/muestras/telas', icon: Shirt, label: 'Telas' },
    { path: '/muestras/hilos', icon: GitBranch, label: 'Hilos' },
    { path: '/muestras/muestra-base', icon: Layers, label: 'Muestras Base' },
    { path: '/muestras/bases', icon: Grid3X3, label: 'Bases' },
    { path: '/muestras/fichas', icon: Tag, label: 'Fichas' },
    { path: '/muestras/tizados', icon: Scissors, label: 'Tizados' },
  ];

  const adminItems = [
    { path: '/muestras/usuarios', icon: Users, label: 'Usuarios' },
    { path: '/muestras/historial', icon: History, label: 'Historial' },
  ];

  const renderMenuItem = (item) => (
    <Link
      key={item.path}
      to={item.path}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
        isActive(item.path)
          ? 'bg-blue-600 text-white'
          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
      }`}
    >
      <item.icon size={18} />
      {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
    </Link>
  );

  // Valor del contexto de permisos
  const permissionContextValue = {
    permissions,
    isAdmin,
    loading: permissionsLoading,
    // Helpers para verificar permisos específicos
    canCreate: (module) => isAdmin || permissions[`${module}_crear`],
    canEdit: (module) => isAdmin || permissions[`${module}_editar`],
    canDelete: (module) => isAdmin || permissions[`${module}_eliminar`],
    canView: (module) => isAdmin || permissions[`${module}_ver`],
  };

  // Pasar user e isAdmin a los children (mantener compatibilidad)
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { 
        muestrasUser: user, 
        muestrasIsAdmin: isAdmin,
        muestrasPermissions: permissions
      });
    }
    return child;
  });

  return (
    <MuestrasPermissionContext.Provider value={permissionContextValue}>
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - Dark Style */}
      <aside className={`${sidebarOpen ? 'w-60' : 'w-16'} bg-gray-900 flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-white font-bold text-lg">Muestras</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white p-1"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map(renderMenuItem)}
          
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                {sidebarOpen && (
                  <span className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Administración
                  </span>
                )}
              </div>
              {adminItems.map(renderMenuItem)}
            </>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-800">
          {sidebarOpen && user && (
            <div className="mb-3">
              <p className="text-white font-medium text-sm">{user.nombre || user.username}</p>
              <p className="text-gray-400 text-xs">{user.rol || 'Usuario'}</p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleGoHome}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              title="Ir al inicio"
            >
              <Home size={16} />
              {sidebarOpen && <span className="text-xs">Inicio</span>}
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
              title="Cerrar sesión"
            >
              <LogOut size={16} />
              {sidebarOpen && <span className="text-xs">Salir</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {childrenWithProps}
      </main>
    </div>
    </MuestrasPermissionContext.Provider>
  );
};

export default MuestrasLayout;
